import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, onMounted, watch, nextTick } from 'vue';
import { ILocationActionsService, Listing, ListingTemplate, ListingType, PreferenceType, SearchRequestBase, SearchType } from '@truckdown/locations';
import { Coordinate, GeocodeResult, IGeocoder } from '@truckdown/systems';

interface IData {
    openedMarker: Ref<string>,
    gMap: Ref<null | HTMLElement>,
    gMapInstance: Ref<google.maps.Map | null>,
    searchPointCenter: Ref<{ lat: number, lng: number }>
}

const getData = function (props: any) {
    return {
        openedMarker: ref<string>(props.startMarker ?? ''),
        gMap: ref<null | HTMLElement>(null),
        gMapInstance: ref<google.maps.Map | null>(null),
        searchPointCenter: ref<{ lat: number, lng: number }>(getCenter(props.request, props.listings))
    } as IData;
}

const iconPrefix = '/listings/locations-components/img/';
const iconSuffix = '-pin.png';

const getIconPreference = function (listing: Listing) {
    if (listing.properties.preference?.type == PreferenceType.Preferred) {
        return '-p';
    } else {
        return '';
    }
}

const customClasses = ['msp', 'mspar', 'kyrish', 'rechtien', 'border', 'bruckners'];

const getIconName = function (listing: Listing) {
    if (listing.properties.useCustomPin) {
        if (customClasses.some((c) => c == listing.properties.customClass)) {
            return listing.properties.customClass;
        }
    }

    switch (listing.properties.template) {
        case ListingTemplate.TDPreferred:
            return 'preferred';
        case ListingTemplate.Elite:
            return 'elite';
        case ListingTemplate.Pro:
            return 'pro';
        case ListingTemplate.Branded:
            return 'branded';
        default:
            return 'default';
    }
}

const getCenterBasedOnListings = function (listings: Listing[]) {
    let lat = 0;
    let lng = 0;
    let length = listings.length;

    if (length == 0) {
        return { lat: lat, lng: lng };
    }

    for (let i = 0; i < listings.length; ++i) {
        lat += listings[i].location.lat;
        lng += listings[i].location.lng;
    }

    lat /= length;
    lng /= length;

    return { lat: lat, lng: lng };
}

const getCenter = function (request: SearchRequestBase, listings: Listing[]) {
    if (request.type == SearchType.Point) {
        return { lat: (request.meta['Lat'] ?? 0) as number, lng: (request.meta['Lng'] ?? 0) as number };
    } else {
        return getCenterBasedOnListings(listings);
    }
}

const searchAreaCircleOptions = ref({
    strokeColor: '#454545',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    fillColor: '#454545',
    fillOpacity: 0.2
});

const searchRadiusCircleOptions = ref({
    strokeColor: '#454545',
    strokeOpacity: 0.6,
    strokeWeight: 1,
    fillOpacity: 0
});

const getComputed = function (request: SearchRequestBase, listings: Listing[]) {
    return {
        center: computed(() => {
            return getCenter(request, listings);
        }),
        enableSearchPoint: computed(() => {
            return request.type == SearchType.Point;
        }),
        searchAreaCircleOptions: searchAreaCircleOptions,
        searchRadiusCircleOptions: searchRadiusCircleOptions
    };
}

const getServiceUrlPortion = function (request: SearchRequestBase): string {
    let sections = window.location.pathname.split('/');
    if (sections.length >= 4 && sections[3].length > 0) {
        return '/' + sections[3];
    }
    return '/services';
}

const getServicesQuery = function (request: SearchRequestBase): string {
    return '?svs=' + request.services.join('-');
}

const getCountryCode = function (code?: string): string | undefined {
    if (code && code.length == 3) {
        if (code == 'CAN') {
            return 'CA';
        }
        if (code == 'USA') {
            return 'US';
        }
        if (code == 'MEX') {
            return 'MX';
        }
    } else if (code?.toLowerCase() == 'us' || code?.toLowerCase() == 'ca' || code?.toLowerCase() == 'mx') {
        return code;
    }
    return undefined;
}

const getLocationQuery = function (request: SearchRequestBase, coord: Coordinate, res?: GeocodeResult): string {
    return '&lat=' + coord.lat
        + '&lng=' + coord.lng
        + '&ctryCd=' + (getCountryCode(res?.address.countryCode) ?? request.meta['CountryCode'])
        + '&rgnCd=' + (res?.address.stateCode ?? request.meta['RegionCode']);
}

const getAmentityQuery = function (request: SearchRequestBase): string {
    if (request.filters.length > 0) {
        return '&ams=' + request.filters.join('-');
    }
    return '';
}

const getOptionsQuery = function (request: SearchRequestBase): string {
    return (request.options.searchByDistance ? '&sod=true' : '')
        + (request.options.suppressManagedServices ? '&sms=true' : '')
        + '&om=true';
}

const getSearchUrl = function (request: SearchRequestBase, coord: Coordinate, res?: GeocodeResult): string {
    return '/search/results'
        + getServiceUrlPortion(request)
        + '/Coordinates'
        + getServicesQuery(request)
        + getLocationQuery(request, coord, res)
        + getAmentityQuery(request)
        + getOptionsQuery(request);
}

const getMethods = function (request: SearchRequestBase, data: IData) {
    return {
        getZIndex: function (id: string | Listing) {
            if (typeof id === 'string' || id instanceof String) {
                if (data.openedMarker.value == id) {
                    return 1000000;
                }
                return 0;
            } else {
                if (data.openedMarker.value == id.id) {
                    return 1000000;
                }

                switch (id.properties.type) {
                    case ListingType.Elite: {
                        return 3;
                    }

                    case ListingType.Pro: {
                        return 2;
                    }

                    case ListingType.Branded: {
                        return 1;
                    }

                    default: {
                        return 0;
                    }
                }
            }

        },
        openMarker: function (id: string) {
            if (data.openedMarker.value == id) {
                data.openedMarker.value = '';
            } else {
                data.openedMarker.value = id;
            }
        },
        closeMarker: function (id: string) {
            return function (ev: Event) {
                if (data.openedMarker.value == id) {
                    data.openedMarker.value = '';
                }
            };
        },
        searchPointDrag: function (ev: google.maps.MapMouseEvent) {
            try {
                if (ev && ev.latLng != null) {
                    data.searchPointCenter.value = {
                        lat: ev.latLng.lat(),
                        lng: ev.latLng.lng()
                    };
                }
            }
            catch (err) {
                console.error(err);
            }
        },
        searchPointDragEnd: function (ev: google.maps.MapMouseEvent) {
            if (ev && ev.latLng != null) {
                let coord = ev.latLng.toJSON();
                context.getService<IGeocoder>('IGeocoder')
                    .then((geocoder) => {
                        return geocoder.geocode(coord)
                            .then((res) => {
                                if (res.length > 0) {
                                    window.location.href = getSearchUrl(request, coord, res[0]);
                                } else {
                                    window.location.href = getSearchUrl(request, coord);
                                }
                            });
                    })
                    .catch((reason) => {
                        console.log('Error running search', reason);
                    });
            }
        },
        getIcon: function (listing: Listing) {
            return iconPrefix + getIconName(listing)
                + getIconPreference(listing) + iconSuffix;
        },
        getCoord: function (listing: Listing) {
            return { lat: listing.location.lat, lng: listing.location.lng };
        },
        showListing: function (listing: Listing) {
            context.getService<ILocationActionsService>('ILocationActionsService')
                .then((service) => {
                    return service.showListing(listing.id)
                        .then((val) => {
                            context.analytics({
                                'event': 'Map',
                                'event_category': 'Listing',
                                'event_label': 'ShowListing'
                            });
                        });
                })
                .catch((reason) => {
                    console.error('Error showing lsiting', reason);
                });
        }
    };
}

const setMapBounds = function (props: any, computed: any, data: IData, map: google.maps.Map) {
    data.gMapInstance.value = map;

    var bounds = new google.maps.LatLngBounds();
    for (let i = 0; i < props.listings.length; i++) {
        bounds.extend({ lat: props.listings[i].location.lat, lng: props.listings[i].location.lng });
    }
    bounds.extend(computed.center.value);
    nextTick(() => {
        map.setZoom(11);
        map.fitBounds(bounds);
    });
}

export default defineComponent({
    name: 'listing-map',
    props: {
        startMarker: String,
        listings: {
            type: Array as () => Listing[],
            required: true
        },
        request: {
            type: Object as () => SearchRequestBase,
            required: true
        }
    },
    emits: [],
    setup: function (props, ctx) {
        let data = getData(props);
        let computed = getComputed(props.request, props.listings);
        let methods = getMethods(props.request, data);

        context.events.on('show-map', function (id: string) {
            data.openedMarker.value = id;
        });

        onMounted(() => {
            if (data.gMap.value != null) {
                let mapLoader = (data.gMap.value as any).$mapPromise as Promise<google.maps.Map>;
                if (mapLoader) {
                    mapLoader.then((map) => {
                        setMapBounds(props, computed, data, map);
                    });
                }
            } else {
                console.log('No map element');
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});

